<template>
  <div>
    <div class="d-md-flex align-center">
      <h1 class="mb-0 mr-3">Affiliate Campaigns</h1>
      <v-btn class="mr-3" @click="addCampaigns = !addCampaigns" color="primary"><v-icon class="mr-2">mdi-plus</v-icon> New campaign</v-btn>
      <v-btn class="mr-3" @click="massCampaignsModel = !massCampaignsModel" color="primary"><v-icon class="mr-2">mdi-plus-box-multiple-outline</v-icon> Mass campaigns</v-btn>
      <span v-if="selected.length">
        <v-btn class="mr-3" @click="massAction('active')" color="primary">Mass activate</v-btn>
        <v-btn class="mr-3" @click="massAction('inactive')" color="primary">Mass deactivate</v-btn>
        <v-btn class="mr-3" @click="exportCampaigns" color="primary">
          <v-progress-circular
                  v-if="exportLoading"
                  :size="25"
                  color="white"
                  indeterminate
          ></v-progress-circular>
          <span v-else>Export</span>
        </v-btn>
      </span>
      <span class="inactive_campaign_text">Show inactive</span>
      <v-switch
          v-model="showInactiveCampaigns"
          class="inactive_campaign_switch"
      >
      </v-switch>
    </div>
    <v-data-table
        :headers="table.headers"
        :header-props="{ sortIcon: null, headerClass: 'white-space-no-wrap' }"
        :items="getCampaigns.data"
        :server-items-length="getCampaigns.count"
        :disable-sort="true"
        :loading="table.loading"
        :footer-props="{itemsPerPageOptions: table.pageOptions}"
        :page="table.currentPage"
        :items-per-page="table.perPage"
        loading-text="Loading... Please wait"
        fixed-header
        :class="`elevation-1 fixed-height co-data-table items-size-${table.heightCounter}`"
        item-class="td_title"
        color="#7F98A5"
        @pagination="updateTablePagination"
        show-select
        v-model="selected"
        @input="onSelect"
    >
      <template v-slot:top>
        <DataTableTopBar @search="search"></DataTableTopBar>
      </template>
      <template v-for="item of table.headers" :slot="`header.${[item.value]}`" slot-scope="{header}">
        <data-filter
                     v-if="table.filters.hasOwnProperty(header.value)"
                     :type="['payIn', 'payOut'].includes(header.value) ? 'range' : (table.filters[header.value]['isDate'] ? 'date' : 'items')"
                     :min="0"
                     :max="1000"
                     :items="table.filters[header.value].data"
                     :entity="header.value"
                     :values="table.filters[header.value].selected"
                     @apply="submitFilter"
                     @search="updateDataInFilters"
        ></data-filter>
        <a v-if="header.sortable === undefined || header.sortable" @click="sortColumn(header.value)">
          <span class="header_title">{{header.text}}</span>
          <v-icon size="16" color="#7F98A5">mdi-arrow-{{header.value === table.orderBy ? table.sortColumnsAsc ? 'up' : 'down' : 'up-down'}}</v-icon>
        </a>
        <span v-else class="header_title">{{header.text}}</span>
      </template>
      <!-- header -->
      <!-- body -->
      <template v-for="header of table.headers" :slot="`item.${[header.value]}`" slot-scope="{item}">
        <template v-if="header.value === 'actions'">
          <v-tooltip v-if="isEditable(item)" top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" v-on="on" icon color="secondary" @click="showCard(item)">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </template>
            <span>Edit</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
                <span
                    @click="copyLink(item)"
                    v-clipboard:copy="campaignUrl"
                    class="td_title"
                    v-bind="attrs"
                    v-on="on"
                >
                  <v-icon size="16" color="#7F98A5" style="cursor:pointer;">mdi-content-copy</v-icon>
                </span>
            </template>
            <span>Copy URL</span>
          </v-tooltip>
        </template>
        <template v-if="header.value === 'currency'">
          <v-tooltip top open-delay="400">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" v-bind:class="{ td_title_inactive_campaign: item.status === 'inactive' }">{{item.currency}}</span>
            </template>
            <span>{{item.currencyName}}</span>
          </v-tooltip>
        </template>
        <template v-else-if="isEntityName(header.value)">
          <span v-bind:class="{ td_title_inactive_campaign: item['status'] === 'inactive' }">
            <a class="entity-link" @click="showCard(item, header.value)">{{item[header.value]}}</a>
          </span>
        </template>
        <template v-else-if="header.value === 'programName'">
           <a class="entity-link" @click="redirectProgram(item)">{{item.programName}}</a>
        </template>
        <template v-else>
          <span v-bind:class="{ td_title_inactive_campaign: item.status === 'inactive' }">
            {{item[header.value]}}
          </span>
        </template>
      </template>
      <!-- body -->
    </v-data-table>
    <DataTableBottomBar
        :showFilter="Object.values(table.filters).find(f => f.selected.length) !== undefined"
        @clearFilters="removeAllFilters"
        v-model="table.heightCounter"
    ></DataTableBottomBar>
    <NewCampaign v-model="addCampaigns" @created="getTableData"></NewCampaign>
    <MassCampaigns v-model="massCampaignsModel"></MassCampaigns>
  </div>
</template>

<script>
  import {mapGetters, mapActions, mapState} from 'vuex'
  import store from '../../../store'
  import {getCookie} from "@/helpers"
  import 'vue-select/dist/vue-select.css'
  import { debounce } from 'debounce'
  import DataTableTopBar from '@/components/common/DataTableTopBar';
  import DataTableBottomBar from '@/components/common/DataTableBottomBar';
  import MassCampaigns from "@/components/pages/affiliates/campaigns/MassCampaigns";
  import campaignsApi from "@/api/campaigns";
  import offerApi from "@/api/offer";
  import affiliatesApi from "@/api/affiliates";
  import NewCampaign from "@/components/pages/affiliates/campaigns/NewCampaign";

  export default {
    props: {
      showSelect: true,
      showActions: {
        default: true
      }
    },
    data() {
      return {
        selected: [],
        heightCounter: 45,
        campaignUrl: '',
        addCampaigns: false,
        massCampaignsModel: false,
        showInactiveCampaigns: false,
        table: {
          heightCounter: 45,
          loading: false,
          searchInCampaigns: '',
          pageChangedByMe: false,
          currentPage: 1,
          perPage: 25,
          pageOptions: [25, 50, 100, 200, 500],
          orderBy: 'dateAdded',
          sortColumnsAsc: false,
          headers: [
            { text: 'Id', value: 'id', class: 'color', align: 'center' },
            { text: 'Name', value: 'name', align: 'left' },
            { text: 'Offer', value: 'offerName', align: 'left' },
            { text: 'Affiliate', value: 'affNameId', align: 'center' },
            { text: 'Status', value: 'status', align: 'center' },
            { text: 'Program', value: 'programName', align: 'center' },
            // { text: 'Pay In', value: 'payIn', align: 'center' },
            // { text: 'Pay Out', value: 'payOut', align: 'center' },
            { text: 'Currency', value: 'currency', align: 'center' },
            { text: 'Affiliate Manager', value: 'affiliateManager', align: 'center' },
            { text: 'Date Added', value: 'dateAdded', align: 'center' },
            { text: 'Actions', value: 'actions', align: 'center', sortable: false },
          ],
          filters: {
            offerName: {selected: [], data: []},
            id: {selected: [], data: []},
            name: {selected: [], data: []},
            affNameId: {selected: [], data: []},
            status: {selected: [], data: []},
            programName: {selected: [], data: []},
            currency: {selected: [], data: []},
            payIn: {selected: [], data: []},
            payOut: {selected: [], data: []},
            dateAdded: {selected: [], data: [], isDate: true},
            affiliateManager: {selected: [], data: []},
          }
        },
        exportLoading: false
      }
    },

    async mounted() {
      await this.getFiltersData()
      if(this.$route.query.filters) {
        this.updateFiltersFromQuery();
      }
      await this.getTableData();
    },
    watch: {
      showInactiveCampaigns: async function () {
        await this.submitFilter()
      },
    },
    methods: {
      ...mapActions('alert', ['ALERT_SUCCESS', 'ALERT_ERROR', 'ALERT_INFO', 'ALERT_WARNING', 'ALERT']),
      getFiltersData() {
        const filters = {};
        for(const name in this.table.filters) {
          campaignsApi.getFilter({
            filter: filters,
            name: name,
          }).then(res => {
            this.table.filters[name].data = res;
          });
        }
      },
      async redirectProgram({programId,programName}) {
        let filters = {
          name: programName
        }
        await store.dispatch('programs/getPrograms', {
          limit: 1,
          page: 0,
          orderBy: true,
          orderDirectionAsc: true,
          filters: filters || null
        });

        const program = this.programs[0];
        if (program) {
          localStorage.setItem("program", JSON.stringify(program));
          await this.$router.push(`/advertisers/programs/edit/${programId}`);
        }
      },
      async getTableData() {
        this.table.loading = true
        this.activateFilters()
        const filters = await this.getFilters();

        await store.dispatch('campaigns/getCampaigns', {
          payload: {
            orderBy: this.table.orderBy,
            orderDirectionAsc: this.table.sortColumnsAsc,
            page: this.table.currentPage,
            limit: this.table.perPage,
            filters: filters || null,
            showInactiveCampaigns: this.showInactiveCampaigns
          }
        })
        this.table.loading = false
      },
      async getFilters() {
        const filters = {};
        filters['search'] = (this.table.searchInCampaigns.length > 0) ? this.table.searchInCampaigns : '';
        for(const name in this.table.filters) {
          filters[name] = (this.table.filters[name].selected.length > 0) ?
              `'${this.table.filters[name].selected.join('\',\'')}'` :
              null;
        }

        return filters;
      },
      updateTablePagination(params){
        if (
            (this.table.currentPage === params.page &&
                this.table.perPage === params.itemsPerPage) ||
            this.table.pageChangedByMe
        ) {
          this.table.pageChangedByMe = false;
          return
        }
        this.table.currentPage = params.page
        this.table.perPage = params.itemsPerPage
        this.getTableData();
      },
      async sortColumn(column) {
        this.table.sortColumnsAsc = (this.table.orderBy === column) ? !this.table.sortColumnsAsc : true;
        this.table.orderBy = column
        await this.getTableData()
      },
      addParamsToLocation(params) {
        const query = JSON.stringify(params) === '{}' ? this.$route.path :
            this.$route.path + '?' + Object.keys(params).map(
            key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
            ).join('&')
        history.pushState({},null, query);
      },
      updateFiltersFromQuery() {
        const queryFilters = JSON.parse(this.$route.query.filters);
        for(const name in this.table.filters) {
          if(queryFilters[name]) {
            this.table.filters[name].selected = queryFilters[name];
          }
        }
      },
      async submitFilter(data, entity) {
        this.table.currentPage = 1;
        this.table.pageChangedByMe = true;
        if(data && entity) {
          this.table.filters[entity].selected = data;
        }
        return await this.getTableData()
      },
      activateFilters() {
        const filtersForQuery = {};
        for (const name in this.table.filters) {
          if(this.table.filters[name].selected.length > 0) {
            filtersForQuery[name] = this.table.filters[name].selected;
          }
        }
        this.addParamsToLocation(
                JSON.stringify(filtersForQuery) !== '{}' ? {filters: JSON.stringify(filtersForQuery)} : {}
        );
      },
      async copyLink(item) {
        this.ALERT_SUCCESS('Link Copied');

         let url =  await store.dispatch('campaigns/getCampaignUrl', {
          token: getCookie('accessToken'),
          params: {
            campaignId: item.id,
            offerId: item.offerId
          }
        })
        this.campaignUrl = url

        await navigator.clipboard.writeText(url)
        setTimeout(()=>{
          this.ALERT_SUCCESS('');
        },3500)
      },

      getProfileLink(nameId) {
        const filter = {affNameId: [nameId]};
        return '/affiliates/profiles?filters='+ encodeURI(JSON.stringify(filter));
      },

      showLink() {
        return localStorage.getItem('workspace') === 'gotzha'
      },

      search(value) {
        this.table.searchInCampaigns = value
        this.submitFilter();
      },

      async updateDataInFilters(data, modalName) {
        if(modalName) {
          campaignsApi.getFilter({
            filter: this.table.filters,
            name: modalName,
            searchFilter: data
          }).then(res => {
            this.table.filters[modalName].data = res;
          });
        }
      },

      isEntityName(item) {
        return ['name', 'offerName', 'affNameId'].includes(item)
      },

      async showCard(item, column) {
        if (column && column === 'offerName') {
          const offer = await offerApi.getOffer(item.offerId);
          if (!offer) {
            return;
          }
          if (offer.type === 'aggregated'){
            this.$router.push(`/advertisers/aggregatedOffer/${offer.id}`);
          } else {
            this.$router.push(`/advertisers/offerV2/${offer.id}`);
          }
          return;
        }

        if (column && column === 'affNameId') {
          const affiliateData = await affiliatesApi.getAffiliatesProfileCO({payload: {
            filters: {
              affNameId: `"${item.affiliateId}"`
            },
            page: 1,
            limit: 1,
            orderBy: 'id',
            orderDirectionAsc: true
          }});
          if (!affiliateData || !affiliateData.data || !affiliateData.data.length) {
            return;
          }
          const affiliate = affiliateData.data[0];

          store.dispatch('cards/ADD', {
            type: 'affiliate',
            title: `[${affiliate.id}] - [${affiliate.name}] : [${item.affiliate}]`,
            key: `affiliate-${affiliate.id}`,
            data: affiliate
          });

          return;
        }

        store.dispatch('cards/ADD', {
          type: 'campaign',
          title: `[${item.id}] - [${item.name}] : [${item.status}]`,
          key: `campaign-${item.id}`,
          data: item
        })
      },

      async removeAllFilters() {
        for (let key in this.table.filters) {
          this.table.filters[key].selected = [];
        }
        await this.getTableData()
      },
      isEditable(item) {
        const userSate = this.$store.state.user;
        if(!userSate || !userSate.user || !userSate.user[0]) {
          return false;
        }
        if (
            userSate.user[0].can('see_all_affiliates_pii') ||
            (userSate.user[0].can('see_own_affiliates_pii') && userSate.user[0].sfl_manager_id === item.affiliateManagerId)
        ) {
          return true
        }
      },
      onSelect($event) {
        this.$emit('selected', $event)
      },

      async massAction(status) {
        await campaignsApi.massAction({status, ids: this.selected.map(i => i.id)});
        await this.getTableData();
        this.selected = this.selected.filter(item => {
          return this.selected.indexOf(item) < 0;
        });
      },

      async exportCampaigns() {
        this.exportLoading = true;
        try {
          const ids = this.selected.map(i => i.id);
          const data = await campaignsApi.getCampaignsGeo(ids);

          if (!data || data.length === 0) {
            console.warn('No campaign data returned from API');
            await this.$dialog.error({
              text: 'No data available for export. Please try again later or check the selected campaigns.',
              title: 'Export Warning'
            });
            this.exportLoading = false;
            return;
          }

          const csvRows = [];
          const headers = Object.keys(data[0]);
          csvRows.push(headers.join(','));

          for (const row of data) {
            const values = headers.map(header => {
              const escaped = ('' + (row[header] ?? '')).replace(/"/g, '""'); // Handle null/undefined values
              return `"${escaped}"`;
            });
            csvRows.push(values.join(','));
          }

          const csv = csvRows.join('\r\n');
          const blob = new Blob([csv], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.setAttribute('hidden', '');
          a.setAttribute('href', url);
          a.setAttribute('download', 'campaigns.csv');
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } catch (error) {
          console.error('Error exporting campaigns:', error);
          await this.$dialog.error({
            text: 'An error occurred while exporting campaigns.',
            title: 'Export Warning'
          });
        } finally {
          this.exportLoading = false;
        }
      }
    },
    computed: {
      ...mapGetters('campaigns', ['getCampaigns', 'getOfferNames', 'getCampaignNames', 'getManagersNamesIds', 'getAffNamesIds', 'getCampaignStatuses', 'getUsers', 'getCampaignsFilter']),
      ...mapGetters('programs', ['programs']),
      ...mapState('affiliates', ['campaignsSflAffiliates']),
    },

    components: {
      DataTableTopBar,
      DataTableBottomBar,
      MassCampaigns,
      NewCampaign
    },

  }
</script>

<style lang="sass">
    .v-input--switch
      margin-top: -3px !important
    .small-tip
      font-size: 12px
      color: #7f98a5
    .search_btn_wrap
      div
        h1
          padding-right: 20px
          margin-bottom: 0
          line-height: 35px
      .add_campaigns_btn
        color: var(--v-primary-darken5) !important
      .search_wrapper
        width: 250px
        padding-bottom: 15px
    .modal_add_campaigns_wrap
      position: fixed
      top: 0
      left: 0
      z-index: 100
      display: flex
      width: 100%
      height: 100%
      justify-content: center
      align-items: center
      background: rgba(0,0,0,.6)
      .modal_add_campaigns
        background: #ffffff
        border-radius: 5px
        box-shadow: 1px 1px 5px 1px #9eaeb6
        padding: 25px 40px
        width: 50%
        height: fit-content
        max-height: 90%
        overflow-y: auto
        margin: 0 auto
        h1
          color: #3a3852
          text-align: center
        >div
          margin-bottom: 15px
          position: relative
          span
            color: #7f98a5
          .custom_select_circular
            position: absolute
            top: 5px
            right: 5px
            background-color: #fff
          .campaigns_name_input
            color: #7f98a5
            height: 31px
            width: 500px
            border: 1px solid rgba(60,60,60,.26)
            border-radius: 4px
            white-space: normal
            padding: 0 10px
            &:focus
              outline: none
          .custom_select
            width: 500px
            cursor: pointer
            .vs__clear
              display: none
            .vs__actions
              cursor: pointer
          .create_campaigns_price
            border: 1px solid rgba(60,60,60,.26)
            border-radius: 4px
            height: 31px
            width: fit-content
            line-height: 31px
            padding: 0
            text-align: center
        .createCampaignBtnWrap
          button
            cursor: pointer
            color: #fff
            height: 36px
            background: #7f98a5
            padding: 5px 45px
            border-radius: 3px
            font-size: 15px
            border: none
            &:hover
              box-shadow: 1px 1px 5px 1px #9eaeb6
            &:last-child
              background: var(--v-primary-base)
              &.disabled_btn
                background: rgba(127, 152, 165, .2)
                cursor: no-drop
                &:hover
                  box-shadow: none
</style>
